/** The protocols with scenario types, each saying whether it is live and what it serves. */

import { Badge } from '@/components/ui/badge';
import { HighlightedText } from '@/components/ui/highlighted-text';
import { cn } from '@/lib/utils';
import { PagedProtocolList } from '@/modules/promotion/paged-protocol-list';
import type { ProtocolSummary } from '@/modules/promotion/types';

/** What the protocol's templates come to, in words. */
const templatesLine = ({ rollup }: ProtocolSummary): string => {
	if (!rollup.promoted && !rollup.templated) return 'No promoted replies yet';
	const parts = [
		rollup.promoted ? `${rollup.promoted} serving` : null,
		rollup.templated ? `${rollup.templated} drafted or off` : null
	];
	return parts.filter(Boolean).join(', ');
};

const ProtocolRow = ({
	protocol,
	selected,
	terms,
	onSelect
}: {
	protocol: ProtocolSummary;
	selected: boolean;
	terms: string[];
	onSelect: (protocol: ProtocolSummary) => void;
}) => (
	<button
		type='button'
		onClick={() => onSelect(protocol)}
		aria-current={selected || undefined}
		className={cn(
			'flex w-full cursor-pointer flex-col gap-1 border-b border-gray-300 px-4 py-3 text-left transition-colors hover:bg-gray-100/60',
			selected && 'bg-gray-100 shadow-[inset_2px_0_0_var(--color-ink)]'
		)}
	>
		<span className='flex items-center justify-between gap-3'>
			<span className='min-w-0 flex-1 truncate text-[13px] font-medium text-text-body capitalize'>
				<HighlightedText text={protocol.name} terms={terms} />
			</span>
			<Badge variant={protocol.live ? 'success' : 'secondary'}>{protocol.live ? 'Live' : 'Not live'}</Badge>
		</span>
		<span className='text-[11px] text-gray-800'>{templatesLine(protocol)}</span>
	</button>
);

interface ProtocolListProps {
	selectedId: string | null;
	onSelect: (protocol: ProtocolSummary) => void;
}

export const ProtocolList = ({ selectedId, onSelect }: ProtocolListProps) => (
	<PagedProtocolList
		emptyMessage='No protocol has scenario types yet.'
		renderRow={(protocol, terms) => (
			<ProtocolRow
				key={protocol.sopId}
				protocol={protocol}
				selected={selectedId === protocol.sopId}
				terms={terms}
				onSelect={onSelect}
			/>
		)}
	/>
);
